import { useChat as useChatBase } from '@ai-sdk/react';
import _ from 'lodash';
import React from 'react';
import { match } from 'ts-pattern';
import type * as TF from 'type-fest';
import {
  type ToolInvocationUIPart,
  type UIMessage,
  type UIMessagePart,
  isDelegateTaskToolInvocation,
  isSubAgentToolInvocation,
} from '#/features/ai';
import { useConfig } from '#/hooks';
import { getAuth0Jwt } from '#/vendors/auth0';
import { formatContextForAPI } from './context-data';
import type { ChatContextItem, ChatData } from './types';

export interface UseChatOptions extends UseChatBaseOptions {
  /**
   * The context to use for the chat.
   */
  context: ChatContextItem[];
  /**
   * The model to use for the chat.
   */
  model: string;
}

export interface UseChatResult extends UseChatBaseResult {
  /**
   * The data appended to the chat.
   */
  data: ChatData;
  /**
   * The messages in the chat.
   */
  messages: Array<UIMessage>;
}

/**
 * A hook for using the AI chat API.
 *
 * @param options - The options for the chat.
 * @returns The chat result.
 */
export function useChat({ model, context, ...options }: UseChatOptions) {
  const config = useConfig();
  const formattedContext = React.useMemo(
    () => formatContextForAPI(context),
    [context]
  );
  const { messages, ...rest } = useChatBase({
    ...options,
    api: `${config.api.url}/_prv/ai/chat`,
    sendExtraMessageFields: true,
    experimental_prepareRequestBody: (body) => {
      return {
        id: body.id,
        context: formattedContext,
        model: getModel(model),
        messages: _.chain(body.messages)
          .filter((message) => message.role === 'user')
          .last()
          .thru((message) => [message])
          .value(),
      };
    },
    fetch: async (url, init) => {
      const token = await getAuth0Jwt();
      return fetch(url, {
        ...init,
        headers: {
          ...init?.headers,
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onError: (error) => {
      console.error('FATAL CHAT ERROR');
      console.error(error);
    },
  }) as UseChatResult;

  const finalMessages = messages.map((message) => ({
    ...message,
    parts: formatPartsForAgentTeams(message.parts),
  }));

  return {
    ...rest,
    messages: finalMessages,
  };
}

/*
|------------------
| Internal
|------------------
*/

type UseChatBaseResult = TF.Simplify<
  Omit<ReturnType<typeof useChatBase>, 'data' | 'messages'>
>;

type UseChatBaseOptions = TF.Simplify<
  Exclude<Parameters<typeof useChatBase>[0], undefined>
>;

function getModel(model: string) {
  return match(model)
    .with('auto', () => 'gpt-4o')
    .otherwise(() => model);
}

/**
 * Group the message parts by agent team, we assume only one agent team per message.
 *
 * @param message - The message to group.
 * @returns The grouped message parts.
 */
function formatPartsForAgentTeams(parts: UIMessagePart[]) {
  const subAgentParts = parts.filter(
    (part) =>
      part.type === 'tool-invocation' &&
      isSubAgentToolInvocation(part.toolInvocation)
  ) as ToolInvocationUIPart[];

  const groupedSubAgentToolInvocations = _.chain(subAgentParts)
    .map((p) => p.toolInvocation)
    .groupBy((p) => p.subAgentName)
    .value();

  const filteredParts = parts.filter((part) => {
    if (part.type !== 'tool-invocation') {
      return true;
    }

    if (isSubAgentToolInvocation(part.toolInvocation)) {
      return false;
    }

    return true;
  });

  return filteredParts.map((part) => {
    if (
      part.type === 'tool-invocation' &&
      isDelegateTaskToolInvocation(part.toolInvocation)
    ) {
      const subAgentParts = [];

      for (const subAgentName in groupedSubAgentToolInvocations) {
        subAgentParts.push({
          name: subAgentName,
          toolInvocations: groupedSubAgentToolInvocations[subAgentName],
        });
      }

      return {
        ...part,
        toolInvocation: {
          ...part.toolInvocation,
          subagents: subAgentParts,
        },
      };
    }

    return part;
  });
}
